import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { api } from '../../../lib/api';

type PeriodKind = 'month' | 'semester' | 'year';
type AwardKind = 'student' | 'class';

type Settings = {
  school_id: number;
  student_month_enabled: boolean;
  student_semester_enabled: boolean;
  student_year_enabled: boolean;
  class_month_enabled: boolean;
  class_semester_enabled: boolean;
  class_year_enabled: boolean;
  student_image_url: string | null;
  class_image_url: string | null;
};

function toAbsoluteUrl(url: string): string {
  const u = (url || '').toString().trim();
  if (!u) return '';
  if (/^https?:\/\//i.test(u)) return u;
  const base = (api.defaults.baseURL || '').toString().replace(/\/+$/, '');
  if (!base) return u;
  return u.startsWith('/') ? `${base}${u}` : `${base}/${u}`;
}

export default function AwardsSettingsModal({
  open,
  onClose,
  onChanged,
}: {
  open: boolean;
  onClose: () => void;
  onChanged?: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null);

  const cacheBust = useMemo(() => String(Date.now()), [open]);

  const [genPeriod, setGenPeriod] = useState<PeriodKind>('month');
  const [genStudent, setGenStudent] = useState(true);
  const [genClass, setGenClass] = useState(true);

  useEffect(() => {
    if (!open) return;
    setErr(null);
    setLoading(true);
    (async () => {
      try {
        const resp = await api.get('/api/community/awards/settings');
        setSettings(resp.data as any);
      } catch (e: any) {
        setErr(e?.response?.data?.detail || 'Nu am putut încărca setările.');
        setSettings(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  const previewStudent = useMemo(() => {
    const u = (settings?.student_image_url || '').trim();
    const base = u || `/uploads/system/award_student.png?v=${encodeURIComponent(cacheBust)}`;
    return toAbsoluteUrl(base);
  }, [cacheBust, settings?.student_image_url]);

  const previewClass = useMemo(() => {
    const u = (settings?.class_image_url || '').trim();
    const base = u || `/uploads/system/award_class.png?v=${encodeURIComponent(cacheBust)}`;
    return toAbsoluteUrl(base);
  }, [cacheBust, settings?.class_image_url]);

  const uploadImage = async (awardKind: AwardKind, file: File) => {
    const form = new FormData();
    form.append('file', file, file.name);
    const resp = await api.post(`/api/community/awards/settings/upload-image?award_kind=${encodeURIComponent(awardKind)}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const url = String((resp.data as any)?.url || '');
    if (!url) throw new Error('Upload failed');
    return url;
  };

  if (!open) return null;

  const setBool = (key: keyof Settings, v: boolean) =>
    setSettings((prev) => (prev ? ({ ...prev, [key]: v } as any) : prev));
  const setStr = (key: keyof Settings, v: string) =>
    setSettings((prev) => (prev ? ({ ...prev, [key]: v } as any) : prev));

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    setErr(null);
    try {
      await api.put('/api/community/awards/settings', {
        student_month_enabled: Boolean(settings.student_month_enabled),
        student_semester_enabled: Boolean(settings.student_semester_enabled),
        student_year_enabled: Boolean(settings.student_year_enabled),
        class_month_enabled: Boolean(settings.class_month_enabled),
        class_semester_enabled: Boolean(settings.class_semester_enabled),
        class_year_enabled: Boolean(settings.class_year_enabled),
        student_image_url: (settings.student_image_url || '').trim() || null,
        class_image_url: (settings.class_image_url || '').trim() || null,
      });
      onChanged?.();
      onClose();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Nu am putut salva setările.');
    } finally {
      setSaving(false);
    }
  };

  const generateNow = async () => {
    setGenerating(true);
    setErr(null);
    try {
      const award_kinds: AwardKind[] = [];
      if (genStudent) award_kinds.push('student');
      if (genClass) award_kinds.push('class');
      if (!award_kinds.length) {
        setErr('Selectează cel puțin un tip (Elev/Clasă).');
        return;
      }
      await api.post('/api/community/awards/generate-now', { period_kind: genPeriod, award_kinds });
      onChanged?.();
      onClose();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Nu am putut genera postările.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-[1200] bg-black/40" onMouseDown={onClose} />
      <div className="fixed inset-0 z-[1210] flex items-center justify-center p-4">
        <div
          className="w-full max-w-3xl bg-card border border-border rounded-xl shadow-xl overflow-hidden"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="p-4 border-b border-border flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-lg font-semibold text-foreground">Setări postări automate</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Se aplică doar pe școala ta. Toți profesorii pot modifica aceste setări.
              </div>
            </div>
            <button className="w-9 h-9 rounded-md hover:bg-muted flex items-center justify-center" onClick={onClose} aria-label="Închide">
              <Icon name="X" size={18} />
            </button>
          </div>

          <div className="p-4 space-y-4">
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="Loader2" size={18} className="animate-spin" />
                <span>Se încarcă…</span>
              </div>
            ) : settings ? (
              <>
                {err ? (
                  <div className="text-sm bg-error/10 border border-error/20 text-error rounded-md p-3">{err}</div>
                ) : null}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border border-border rounded-lg p-4">
                    <div className="font-semibold text-foreground">Elevul perioadei</div>
                    <div className="mt-3 space-y-2">
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={Boolean(settings.student_month_enabled)}
                          onChange={(e) => setBool('student_month_enabled', e.target.checked)}
                        />
                        <span>Lunar</span>
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={Boolean(settings.student_semester_enabled)}
                          onChange={(e) => setBool('student_semester_enabled', e.target.checked)}
                        />
                        <span>Semestrial</span>
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={Boolean(settings.student_year_enabled)}
                          onChange={(e) => setBool('student_year_enabled', e.target.checked)}
                        />
                        <span>Anual</span>
                      </label>
                      <div className="pt-2">
                        <Input
                          label="Imagine (URL) – opțional"
                          placeholder="Lasă gol pentru imaginea implicită"
                          value={(settings.student_image_url || '').toString()}
                          onChange={(e) => setStr('student_image_url', e.target.value)}
                        />
                        <div className="mt-2 flex items-center gap-2">
                          <label className="inline-flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={async (e) => {
                                const f = e.target.files?.[0];
                                e.target.value = '';
                                if (!f) return;
                                try {
                                  setErr(null);
                                  const url = await uploadImage('student', f);
                                  setStr('student_image_url', url);
                                } catch (ex: any) {
                                  setErr(ex?.message || 'Nu am putut încărca imaginea.');
                                }
                              }}
                            />
                            <span className="px-3 py-2 rounded-md border border-border bg-card hover:bg-muted text-foreground">
                              Încarcă imagine
                            </span>
                          </label>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setStr('student_image_url', '')}
                          >
                            Revino la implicit
                          </Button>
                        </div>
                        <div className="mt-2">
                          <div className="w-full rounded-md border border-border bg-muted/20 aspect-[1200/630] overflow-hidden flex items-center justify-center">
                            <img src={previewStudent} alt="Previzualizare elev" className="w-full h-full object-contain" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border border-border rounded-lg p-4">
                    <div className="font-semibold text-foreground">Clasa perioadei</div>
                    <div className="mt-3 space-y-2">
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={Boolean(settings.class_month_enabled)}
                          onChange={(e) => setBool('class_month_enabled', e.target.checked)}
                        />
                        <span>Lunar</span>
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={Boolean(settings.class_semester_enabled)}
                          onChange={(e) => setBool('class_semester_enabled', e.target.checked)}
                        />
                        <span>Semestrial</span>
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={Boolean(settings.class_year_enabled)}
                          onChange={(e) => setBool('class_year_enabled', e.target.checked)}
                        />
                        <span>Anual</span>
                      </label>
                      <div className="pt-2">
                        <Input
                          label="Imagine (URL) – opțional"
                          placeholder="Lasă gol pentru imaginea implicită"
                          value={(settings.class_image_url || '').toString()}
                          onChange={(e) => setStr('class_image_url', e.target.value)}
                        />
                        <div className="mt-2 flex items-center gap-2">
                          <label className="inline-flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={async (e) => {
                                const f = e.target.files?.[0];
                                e.target.value = '';
                                if (!f) return;
                                try {
                                  setErr(null);
                                  const url = await uploadImage('class', f);
                                  setStr('class_image_url', url);
                                } catch (ex: any) {
                                  setErr(ex?.message || 'Nu am putut încărca imaginea.');
                                }
                              }}
                            />
                            <span className="px-3 py-2 rounded-md border border-border bg-card hover:bg-muted text-foreground">
                              Încarcă imagine
                            </span>
                          </label>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setStr('class_image_url', '')}
                          >
                            Revino la implicit
                          </Button>
                        </div>
                        <div className="mt-2">
                          <div className="w-full rounded-md border border-border bg-muted/20 aspect-[1200/630] overflow-hidden flex items-center justify-center">
                            <img src={previewClass} alt="Previzualizare clasă" className="w-full h-full object-contain" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border border-border rounded-lg p-4">
                  <div className="font-semibold text-foreground">Generează acum (o singură dată)</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Creează postările pentru perioada curentă (dacă nu există deja).
                  </div>
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                    <div className="space-y-1">
                      <div className="text-xs text-muted-foreground">Perioadă</div>
                      <select
                        className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground"
                        value={genPeriod}
                        onChange={(e) => setGenPeriod(e.target.value as PeriodKind)}
                      >
                        <option value="month">Lună</option>
                        <option value="semester">Semestru</option>
                        <option value="year">An</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={genStudent} onChange={(e) => setGenStudent(e.target.checked)} />
                        <span>Elev</span>
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={genClass} onChange={(e) => setGenClass(e.target.checked)} />
                        <span>Clasă</span>
                      </label>
                    </div>
                    <Button variant="outline" loading={generating} onClick={() => void generateNow()} iconName="Wand2">
                      Generează
                    </Button>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2">
                  <Button variant="outline" onClick={onClose}>
                    Anulează
                  </Button>
                  <Button variant="default" loading={saving} onClick={() => void save()} iconName="Save">
                    Salvează
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-sm text-error">{err || 'Nu există setări.'}</div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
